'use client'

import { useEffect } from 'react'

const pickSrc = (img: HTMLImageElement): string | null => {
  const srcset = img.getAttribute('srcset')

  // 1️⃣ Use srcset if available
  if (srcset) {
    const dpr = window.devicePixelRatio || 1

    const candidates = srcset
      .split(',')
      .map((entry) => {
        const [url, descriptor] = entry.trim().split(/\s+/)
        if (!url) return null

        const scale = descriptor?.endsWith('x') ? parseFloat(descriptor) : 1

        return { url, scale }
      })
      .filter(Boolean) as { url: string; scale: number }[]

    if (candidates.length) {
      // pick closest >= DPR, else largest available
      return candidates.find((c) => c.scale >= dpr)?.url ?? candidates[candidates.length - 1].url
    }
  }

  // 2️⃣ Fallback to src
  return img.src || null
}

const preloaded = new Set<string>()

export const prefetchImages = (selector: string) => {
  document.querySelectorAll<HTMLImageElement>(selector).forEach((img) => {
    const src = pickSrc(img)
    if (!src) return
    if (!preloaded.has(src)) {
      preloaded.add(src)
    }

    const link = document.createElement('link')
    link.rel = 'preload'
    link.as = 'image'
    link.href = src
    link.fetchPriority = 'low'

    document.head.appendChild(link)
  })
}

// images set to loading=lazy and data-load-time="idle" will loaded when the browser is idle
export const ImagePrefetch = () => {
  useEffect(() => {
    if (!('requestIdleCallback' in window)) {
      setTimeout(() => prefetchImages('img[data-load-time="idle"]'), 2000)
      return
    }

    requestIdleCallback(() => prefetchImages('img[data-load-time="idle"]'), { timeout: 3000 })
  }, [])

  return null
}
